import type { FileViewerState } from "@/redux/fileViewerSlice";

/** The `highlight` packet PdfReader posts into the pdf.js iframe. The
 *  viewer scrolls to `fileViewerData.highlightPageNum` and highlights the
 *  passage described by the text-match fields. */
export interface HighlightMessage {
  highlight: { fileViewerData: FileViewerState };
}

// `!= null` throughout: the slice's fields are typed `| null`, but its
// reducers merge untyped payloads and real dispatch sites write
// `... || undefined` into them, so runtime state carries both.
const hasText = (value: string | null | undefined): boolean =>
  value != null && String(value).trim() !== "";

/** Build the highlight packet, or null when the state carries nothing the
 *  viewer can act on. Without this gate every message means "scroll to
 *  page 1" to the viewer, which is how the agents outputs preview (which
 *  never sets highlight data) kept losing the user's scroll position
 *  (#11266). A target is an explicit page or a non-blank highlightContext —
 *  the viewer's highlightTextNodes.js requires the context and returns early
 *  without one; originalNumValue / parentElementText only refine the match
 *  within it. */
export const buildHighlightMessage = (
  fileViewerData: FileViewerState
): HighlightMessage | null => {
  const hasTarget =
    fileViewerData.highlightPageNum != null ||
    hasText(fileViewerData.highlightContext);
  if (!hasTarget) return null;
  return { highlight: { fileViewerData } };
};
